import { useState } from "react";
import { motion } from "framer-motion";
import { MapPin, Clock, Music2 } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { LINKS, INFO } from "@/lib/constants";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const [form, setForm] = useState({ name: "", phone: "", message: "" });
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.message.trim()) {
      toast.error("Bitte Name und Nachricht ausfüllen.");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Nachricht gesendet! Wir melden uns bei dir.");
      setForm({ name: "", phone: "", message: "" });
    } catch {
      toast.error("Fehler beim Senden. Bitte versuch es erneut.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="kontakt" className="py-28 lg:py-36" style={{ background: "#111111" }}>
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <motion.p initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ duration: 0.7 }} className="pre-headline mb-6">
          Standort & Kontakt
        </motion.p>
        <motion.h2
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="font-display font-bold text-3xl sm:text-4xl lg:text-5xl mb-16"
          data-testid="contact-headline"
        >
          Du findest uns <span className="gold-text">hier.</span>
        </motion.h2>

        <div className="grid lg:grid-cols-2 gap-14">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <div className="space-y-8">
              <div className="flex gap-5 items-start" data-testid="contact-address">
                <MapPin size={22} className="gold-text mt-1 shrink-0" />
                <div>
                  <p className="font-medium">{INFO.address}</p>
                  <p style={{ color: "#999999" }}>{INFO.city}</p>
                </div>
              </div>
              <div className="flex gap-5 items-start" data-testid="contact-hours">
                <Clock size={22} className="gold-text mt-1 shrink-0" />
                <div>
                  <p className="font-medium">{INFO.hoursWeek}</p>
                  <p style={{ color: "#999999" }}>{INFO.hoursSat}</p>
                </div>
              </div>
              <div className="flex gap-5 items-start" data-testid="contact-tiktok">
                <Music2 size={22} className="gold-text mt-1 shrink-0" />
                <a href={LINKS.tiktok} target="_blank" rel="noopener noreferrer" className="font-medium hover:underline" style={{ color: "#E8CC7A" }}>
                  TikTok: {INFO.tiktokHandle}
                </a>
              </div>
              <a href={LINKS.maps} target="_blank" rel="noopener noreferrer" className="btn-ghost inline-block" data-testid="contact-maps-cta">
                In Google Maps öffnen
              </a>
            </div>

            <form onSubmit={submit} className="mt-14 space-y-4" data-testid="contact-form">
              <p className="font-display text-xl font-bold mb-2">Schreib uns direkt</p>
              <div className="grid sm:grid-cols-2 gap-4">
                <input
                  data-testid="contact-input-name"
                  placeholder="Dein Name *"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                />
                <input
                  data-testid="contact-input-phone"
                  placeholder="Telefon (optional)"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                />
              </div>
              <textarea
                data-testid="contact-input-message"
                placeholder="Deine Nachricht *"
                rows={4}
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
              />
              <button type="submit" disabled={sending} className="btn-gold disabled:opacity-60" data-testid="contact-submit-button">
                {sending ? "Wird gesendet…" : "Nachricht senden"}
              </button>
            </form>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="min-h-[420px]"
            style={{ border: "1px solid rgba(201,168,76,0.25)" }}
          >
            <iframe
              title="Nabil Barbershop Standort"
              src={LINKS.mapsEmbed}
              className="w-full h-full min-h-[420px] map-dark"
              style={{ border: 0 }}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              data-testid="contact-map"
            />
          </motion.div>
        </div>
      </div>
    </section>
  );
}
